//@import dependencies
import React, {useState, useEffect} from 'react'
import moment from 'moment';
//@end

//@import components
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import InputComponent from 'app_components/commons/Input/InputComponent'
import ForumCard from 'app_components/commons/ForumCard/ForumCard'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import ForumResponseModule from 'app_components/modules/ForumResponseModule/ForumResponseModule'
import PublicAppContainer from 'app_components/layouts/public'
import { Link, useParams } from 'react-router-dom'
import ModalFormForum from 'app_components/modules/ModalFormForum/ModalFormForum'
import ButtonWithLogin from 'app_components/modules/ButtonWithLogin/ButtonWithLogin';
//@end

// @import hooks
import { useTranslation } from 'react-i18next'
// @end

// @INFO Import types
import { ForumMessage } from 'app_types/admin/forum/forumMessageTypes';
import { IForumListItem } from 'app_types/admin/forum/forumTypes';
// @end

// @INFO Import services
import { routingService } from 'app_services/routingService'
import ForumMessageService from 'app_services/admin/forum/forumMessageService';
import ForumService from 'app_services/admin/forum/forumService'
// @end

// @INFO Import Styles
import './../forumScreen.scss'
import { CombinedReducers } from 'app_events/index';
import { useSelector } from 'react-redux';
import AlertComponent from 'app_components/commons/alert';
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import ShareModule from 'app_components/modules/ShareModule/ShareModule';
import { customs } from 'app_config/globals';

// @ADD Component types
interface IForumScreenLayoutProps {
  history?: any
}

const ForumScreenLayout: React.FC<IForumScreenLayoutProps> = (props) => {

  const { t } = useTranslation()
  const [forum, updateForum] = useState<IForumListItem | undefined>(undefined);
  const [openNewForm, updateOpenNewForm] = useState<boolean>(false);
  const [openInput, updateOpenInput] = useState<boolean>(false);
  const [message, updateMessage] = useState<string>('');
  const [loadingSend, updateLoadingSend] = useState<boolean>(false);
  const [messages, updateMessages] = useState<ForumMessage[]>([]);
  const [bestMessage, updateBestMessage] = useState<ForumMessage | undefined>(undefined);
  const [forumsRelated, updateForumsRelated] = useState<IForumListItem[]>([]);
  const [searchStr, updateSearchStr] = useState<string>('');
  const [forums, updateForums] = useState<IForumListItem[]>([]);
  const params = useParams<{id?: string}>();
  const user = useSelector((store: CombinedReducers) => store.auth.user)

  // @INFO Servicios
  const forumService = new ForumService();
  const forumMessageService = new ForumMessageService();
  const screenNotification = new ScreenNotificationsUtil();

  useEffect(() => {
    getForum();
  }, [])

  useEffect(() => {
    if (forum?._id) {
      getMessages();
      getBestMessage();
      getRelatedForums();
    }
  }, [forum])

  const getForum = async () => {
    if (!params.id) return;
    const response: any = await forumService.getOne(params.id);
    if (response) {
      updateForum(response);
    }
  }

  const getMessages = async () => {
    if (!forum?._id) return
    const response = await forumMessageService.list({
      forum: forum?._id,
      user: user?.data?.id
    });
    if (response?.code === 200 && response?.messages) {
      updateMessages(response.messages)
    }
  }

  const getBestMessage = async () => {
    if (!forum?._id) return
    const response = await forumMessageService.getBetterMessage({
      forum: forum._id,
      user: user?.data?.id
    });
    if (response?.code === 200 && response.message) {
      updateBestMessage(response.message)
    }
  }

  const getRelatedForums = async () => {
    if (!forum?._id) return
    const response: any = await forumService.getRelatedForums({
      forum: forum._id
    })
    if (response?.code === 200 && response?.forums?.length) {
      updateForumsRelated(response.forums)
    }
  }

  const handleClickResponse = async () => {
    if (openInput) {
      if (!user?.data?.id || !forum?._id) return
      // Enviar una respuesta al foro
      updateLoadingSend(true);
      const response = await forumMessageService.sendMessage({
        user: user?.data?.id,
        forum: forum?._id,
        message
      });
      if (response?.code === 200 && response?.message) {
        addNewMessage(response.message)
      }
      updateLoadingSend(false);
      updateMessage('');
      updateOpenInput(false);
    } else {
      updateOpenInput(true);
    }
  }

  const addNewMessage = (_message: ForumMessage) => {
    updateMessages([
      _message,
      ...messages
    ])
  }

  const handleNewLike = (id: string, response?: any) => {
    // Actualizar el array de mensajes
    const idx = messages.findIndex((m) => m._id === id)
    if (idx >= 0) {
      const newMessages = [...messages];
      const currentLikes = newMessages[idx].totalLikes
      if (currentLikes !== undefined) {
        newMessages[idx].totalLikes = currentLikes+1
      } else {
        newMessages[idx].totalLikes = 1
      }
      newMessages[idx].postedLike = true;
      updateMessages([...newMessages]);
    }
    // Actualizar el mejor mensaje en caso de que exista
    if (bestMessage && bestMessage._id === id) {
      const newBestMessage = {...bestMessage};
      const currentLikes = newBestMessage.totalLikes
      if (currentLikes !== undefined) {
        newBestMessage.totalLikes = currentLikes+1
      } else {
        newBestMessage.totalLikes = 1
      }
      newBestMessage.postedLike = true;
      updateBestMessage({...newBestMessage});
    }
  }

  const handleCloseModal = async (data?: {response?: any, created?: boolean}) => {
    if (data?.created) {
      await screenNotification.showSuccessTimer({
        text: 'El foro ha sido creado exitosamente'
      })
      // Redirigir a la pantalla del foro
      if (props.history && data?.response?._id) {
        props.history.push(`${routingService.getPublicPath(`/forum/${data.response?._id}`)}`)
        window.location.reload()
      }
    }
    updateOpenNewForm(false);
  }

  const handleSearchForums = async () => {
    if (searchStr.trim().length) {
      const paramsSearch: any = {
        page: 1,
        perPage: 4,
        isActive: true,
        search: searchStr,
      }
      const response = await forumService.list(paramsSearch);
      // Actualizar el paginador y guardar los items si llega una respuesta exitosa
      if (response?.forums) {
        updateForums(response?.forums)
      }
    } else {
      updateForums([])
    }
  }

  const handleLoginSuccess = () => {
    getMessages();
    getBestMessage();
  }

  return(
    <PublicAppContainer>
      <div className='forum_screen_landing-layout'>
        <TextComponent
          type='subtitle'
        >
          Foro
        </TextComponent>
        <TextComponent
          type='title'
        >
          {forum?.title}
        </TextComponent>
        <div className="container-inline-text">
          <TextComponent
            type='text'
            style={{fontWeight: 300, fontSize: '0.8rem'}}
          >
            Autor: {forum?.created_by?.profile?.first_name} {forum?.created_by?.profile?.last_name}
          </TextComponent>
          <TextComponent
            type='text'
            style={{fontWeight: 300, fontSize: '0.8rem'}}
          >
            Fecha: {moment(forum?.created_at).format('DD/MM/YYYY')}
          </TextComponent>
        </div>
        <div className="container-inline-text">
          <TextComponent
            type='text'
            style={{fontWeight: 300, fontSize: '0.8rem'}}
          >
            {forum?.category?.name}
          </TextComponent>
          {forum?.tags?.length ?
            forum?.tags.map((c) => (
              <TextComponent
                type='text'
                style={{fontWeight: 300, fontSize: '0.8rem'}}
              >
                {c.name}
              </TextComponent>
            ))
          :null}
        </div>
        <div className="container-content-page">
          <div>
            <div className="header-forum-content">
              <img src={forum?.coverUrl} alt="" />
              <TextComponent
                type='text'
              >
                {forum?.description}
              </TextComponent>
            </div>
            <hr />
            <div className="container-action-response-forum">
              <div className="container-buttons-response">
                <ButtonWithLogin
                  variant='secondary'
                  onClick={handleClickResponse}
                  loginText='Es necesario iniciar sesión como docente para crear una respuesta'
                  loading={loadingSend}
                  onLoginSuccess={handleLoginSuccess}
                  disabled={openInput && !message.trim().length}
                  permissions={[
                    {
                      module: 'module:forums',
                      permissions: ['permission:forums_response']
                    }
                  ]}
                >
                  Responder
                </ButtonWithLogin>
                {openInput ?
                  <ButtonComponent
                    variant='tertiary'
                    onClick={() => updateOpenInput(false)}
                  >
                    Cancelar
                  </ButtonComponent>
                :null}
                <ShareModule
                  share={customs.site}
                />
              </div>
              {openInput ?
                <InputComponent
                  placeholder='Escribe tu respuesta'
                  onChange={(e) => updateMessage(e.target.value)}
                  value={message}
                />
              :null}
            </div>
            <div className="container-responses">
              {bestMessage ?
                <>
                  <TextComponent
                    type='title'
                    style={{textAlign: 'center'}}
                  >
                    Mejor respuesta
                  </TextComponent>
                  <ForumResponseModule
                    message={bestMessage}
                    subMessages={messages.filter((m2) => m2.forumMessage === bestMessage._id)}
                    onNewResponse={(_m) => addNewMessage(_m)}
                    onLikeSaved={() => handleNewLike(bestMessage._id)}
                    onLoginSuccess={handleLoginSuccess}
                  />
                </>
              :null}
              <TextComponent
                type='title'
                style={{textAlign: 'center'}}
              >
                Comentarios
              </TextComponent>
              {messages?.length ?
                messages?.filter((m) => !m.forumMessage && m._id !== bestMessage?._id).map((m, idxM) => (
                  <ForumResponseModule
                    message={m}
                    subMessages={messages.filter((m2) => m2.forumMessage === m._id)}
                    onNewResponse={(_m) => addNewMessage(_m)}
                    onLikeSaved={() => handleNewLike(m._id)}
                    onLoginSuccess={handleLoginSuccess}
                    key={m._id}
                  />
                ))
              :
                <AlertComponent description='Este foro aún no tiene comentarios' />
              }
            </div>
            <div className="container-new-question">
              <ButtonWithLogin
                variant='tertiary'
                onClick={() => updateOpenNewForm(true)}
                onLoginSuccess={handleLoginSuccess}
                permissions={[
                  {
                    module: 'module:forums',
                    permissions: ['permission:forums_create']
                  }
                ]}
              >
                Añadir pregunta
              </ButtonWithLogin>
            </div>
          </div>
          <div
            className='container-related-forums'
          >
            <InputComponent
              placeholder='Buscar en foros'
              onChange={(e) => updateSearchStr(e.target.value)}
              value={searchStr}
              onEnter={handleSearchForums}
            />
            <TextComponent
              type='subtitle'
              style={{color: 'var(--primary)', fontWeight: 600, textTransform: 'uppercase'}}
            >
              Temas relacionados
            </TextComponent>
            <hr className='line-primary' />
            {forums?.length ?
              forums.map((f) => (
                <ForumCard
                  key={f._id}
                  forumId={f._id}
                  title={f.title}
                  description={f.description}
                  reload
                  date={f.postDate}
                />
              ))
            :
            (forumsRelated?.length ?
              forumsRelated.map((f) => (
                <ForumCard
                  key={f._id}
                  forumId={f._id}
                  title={f.title}
                  description={f.description}
                  reload
                  date={f.postDate}
                />
              ))
            :
              <AlertComponent description='No hay foros relacionados' />
            )}
            <Link
              to={routingService.getPublicPath('/forums')}
              style={{textDecoration: 'none'}}
            >
              <TextComponent
                type='text'
                style={{color: 'var(--secondary)', textAlign: 'center', textDecoration: 'underline'}}
              >
                Ver todos
              </TextComponent>
            </Link>
          </div>
        </div>
        <ModalFormForum
          open={openNewForm}
          onClose={handleCloseModal}
        />
      </div>
    </PublicAppContainer>
  )
}

export default ForumScreenLayout
